import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import {
  fetchEntity,
  fetchEntityDependents,
  fetchTask,
  type ApiRequestError,
} from '../api/client.js';
import {
  buildLessonTranscript,
  type LessonTranscript,
} from '../lib/lessonTranscript.js';

import { RawStoredField, WarningList, ResponseBlock, StepBlock } from './TranscriptBlocks.js';

type LoadedDocument = Awaited<ReturnType<typeof fetchEntity>> | Awaited<ReturnType<typeof fetchTask>>;
type EntityReference = Awaited<ReturnType<typeof fetchEntityDependents>>[number];

type LoadState =
  | { status: 'loading' }
  | { status: 'loaded'; document: LoadedDocument; transcript: LessonTranscript; fetchedAt: Date }
  | { status: 'error'; errorStatus: number | undefined };

interface DependencyReference {
  id: number;
  title: string | null;
}

interface DependencyState {
  incomingStatus: 'loading' | 'loaded' | 'error';
  incoming: EntityReference[];
  outgoingStatus: 'loading' | 'loaded';
  outgoing: DependencyReference[];
}

const RETIRED_TITLE_PATTERN = /^\s*(\[DELETED\]|DELETED\b)/;

function isRetiredByTitle(title: unknown): boolean {
  return typeof title === 'string' && RETIRED_TITLE_PATTERN.test(title);
}

/**
 * A related lesson's `title` comes back as stored, and the API does not coerce a malformed one
 * (`content.service.ts` preserves it). Rendering a non-string directly as a React child throws
 * "Objects are not valid as a React child", and the route error boundary would then replace an
 * otherwise perfectly good transcript the moment the dependency request resolved.
 */
function safeTitle(value: unknown): string | null {
  if (typeof value === 'string') return value || null;
  if (value == null) return null;
  return '(stored title is not text)';
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return value !== null && typeof value === 'object' && !Array.isArray(value);
}

function errorStatus(reason: unknown): number | undefined {
  const apiReason = reason as ApiRequestError | null;
  if (apiReason && typeof apiReason.status === 'number') return apiReason.status;
  return undefined;
}

function prerequisiteIds(document: LoadedDocument): number[] {
  const prerequisites = (document as unknown as Record<string, unknown>).prerequisites;
  if (!isRecord(prerequisites)) return [];
  return Object.keys(prerequisites)
    .map(Number)
    .filter((id) => Number.isFinite(id) && Number.isInteger(id) && id >= 0)
    .sort((left, right) => left - right);
}

function publicationLabel(transcript: LessonTranscript): string {
  if (transcript.publishedInvalid) return 'Invalid stored value';
  if (transcript.published === true) return 'Published';
  if (transcript.published === false) return 'Draft';
  return 'Publication state not stated';
}

function TaskLifecycle({ document, transcript }: { document: LoadedDocument; transcript: LessonTranscript }) {
  const raw = document as unknown as Record<string, unknown>;
  const deleted = raw.deleted;
  const titleRetired = isRetiredByTitle(raw.title);
  const suspiciousUnmatchedTitle = typeof raw.title === 'string'
    && (/^\s*DEELTED\b/i.test(raw.title) || (!titleRetired && /\[DELETED\]/i.test(raw.title)));
  const deletedInvalid = deleted !== undefined && deleted !== null && typeof deleted !== 'boolean';
  const contradictoryPublished = (deleted === true || titleRetired) && transcript.published === true;
  const ambiguous = deletedInvalid || suspiciousUnmatchedTitle || contradictoryPublished;
  const deletedLabel = deletedInvalid
    ? 'Invalid stored value'
    : deleted === true ? 'Set to true (deleted)' : deleted === false ? 'Set to false' : 'Not present';

  return (
    <section className="rounded-xl border border-amber-200 bg-amber-50 p-4" data-testid="task-lifecycle">
      <h2 className="text-sm font-bold uppercase tracking-wide text-amber-900">Task lifecycle facts</h2>
      <ul className="mt-2 space-y-1 text-sm text-amber-950">
        <li>Deleted flag: {deletedLabel}</li>
        <li>Title-retired marker: {titleRetired ? 'Detected' : 'Not detected'}</li>
        <li>Published field: {publicationLabel(transcript)}</li>
        <li>Retirement status ambiguous: {ambiguous ? 'Yes — the stored signals are malformed, unmatched, or contradictory.' : 'No conflicting stored signals detected.'}</li>
      </ul>
    </section>
  );
}

function DependentsBlock({ state }: { state: DependencyState }) {
  return (
    <section className="rounded-2xl border border-slate-200 bg-white p-5" data-testid="transcript-dependencies">
      <h2 className="font-serif text-xl font-semibold text-slate-950">Dependency facts for deletion review</h2>
      <div className="mt-4 grid gap-5 md:grid-cols-2">
        <div>
          <h3 className="text-xs font-bold uppercase tracking-wide text-slate-500">Outgoing prerequisites</h3>
          {state.outgoingStatus === 'loading' ? <p className="mt-2 text-sm text-slate-500">Loading prerequisite titles…</p>
            : state.outgoing.length === 0 ? <p className="mt-2 text-sm text-slate-500">No outgoing prerequisites are stored.</p>
              : <ul className="mt-2 space-y-1 text-sm text-slate-800">{state.outgoing.map((item) => <li key={item.id}>{item.title ?? 'Title unavailable'} · id {item.id}</li>)}</ul>}
        </div>
        <div>
          <h3 className="text-xs font-bold uppercase tracking-wide text-slate-500">Incoming dependents</h3>
          {state.incomingStatus === 'loading' ? <p className="mt-2 text-sm text-slate-500">Loading lessons that depend on this one…</p>
            : state.incomingStatus === 'error' ? (
              <p className="mt-2 rounded-lg border border-rose-300 bg-rose-50 p-3 text-sm font-semibold text-rose-950">
                Dependency impact unavailable — do not use this page alone to approve a deletion.
              </p>
            ) : state.incoming.length === 0 ? <p className="mt-2 text-sm text-slate-500">No incoming dependents were returned.</p>
              : <ul className="mt-2 space-y-1 text-sm text-slate-800">{state.incoming.map((item) => <li key={item.id}>{safeTitle(item.title) ?? 'Untitled lesson'} · id {item.id}</li>)}</ul>}
        </div>
      </div>
      <p className="mt-4 text-xs text-slate-500">
        These are relationship facts, not a deletion verdict, and they are <strong>not the whole
        impact</strong>: archiving a lesson also detaches or re-orphans the practice items attached
        to it, and those are not listed here. The archive operation itself remains the final safety
        authority.
      </p>
    </section>
  );
}

export function LessonTranscriptPage({ kind }: { kind: 'entity' | 'task' }) {
  const params = useParams();
  const courseId = params.courseId;
  const requestedIdText = kind === 'entity' ? params.entityId : params.taskId;
  const requestedId = Number(requestedIdText);
  const invalidRoute = !courseId || !requestedIdText || !Number.isFinite(requestedId);
  const [retryToken, setRetryToken] = useState(0);
  const [loadState, setLoadState] = useState<LoadState>({ status: 'loading' });
  const [dependencies, setDependencies] = useState<DependencyState>({
    incomingStatus: 'loading', incoming: [], outgoingStatus: 'loading', outgoing: [],
  });

  useEffect(() => {
    if (invalidRoute || !courseId) return;
    let cancelled = false;
    setLoadState({ status: 'loading' });
    const request = kind === 'entity' ? fetchEntity(courseId, requestedId) : fetchTask(courseId, requestedId);
    void request
      .then((document) => {
        if (!cancelled) {
          setLoadState({
            status: 'loaded',
            document,
            transcript: buildLessonTranscript(document, kind),
            fetchedAt: new Date(),
          });
        }
      })
      .catch((reason: unknown) => {
        if (!cancelled) setLoadState({ status: 'error', errorStatus: errorStatus(reason) });
      });
    return () => { cancelled = true; };
  }, [courseId, invalidRoute, kind, requestedId, retryToken]);

  useEffect(() => {
    if (kind !== 'entity' || !courseId || loadState.status !== 'loaded') return;
    let cancelled = false;
    const ids = prerequisiteIds(loadState.document);
    setDependencies({ incomingStatus: 'loading', incoming: [], outgoingStatus: 'loading', outgoing: [] });

    void fetchEntityDependents(courseId, requestedId)
      .then((incoming) => {
        if (!cancelled) setDependencies((current) => ({ ...current, incomingStatus: 'loaded', incoming }));
      })
      .catch(() => {
        if (!cancelled) setDependencies((current) => ({ ...current, incomingStatus: 'error', incoming: [] }));
      });

    void Promise.all(ids.map(async (id): Promise<DependencyReference> => {
      try {
        const prerequisite = await fetchEntity(courseId, id);
        return { id, title: safeTitle(prerequisite.title) };
      } catch {
        return { id, title: null };
      }
    })).then((outgoing) => {
      if (!cancelled) setDependencies((current) => ({ ...current, outgoingStatus: 'loaded', outgoing }));
    });

    return () => { cancelled = true; };
  }, [courseId, kind, loadState, requestedId]);

  const singular = kind === 'entity' ? 'lesson' : 'task';
  if (invalidRoute) {
    return <main className="flex-1 overflow-y-auto p-6"><h1 className="text-xl font-bold text-rose-900">Invalid {kind} route</h1></main>;
  }
  if (loadState.status === 'loading') {
    return <main className="flex-1 overflow-y-auto p-6 text-slate-600">Loading {singular} transcript…</main>;
  }
  if (loadState.status === 'error') {
    if (loadState.errorStatus === 404) {
      return <main className="flex-1 overflow-y-auto p-6"><h1 className="text-xl font-bold text-slate-950">No {singular} with id {requestedId} in course {courseId}. It may have been archived.</h1></main>;
    }
    if (loadState.errorStatus === 403) {
      return <main className="flex-1 overflow-y-auto p-6"><h1 className="text-xl font-bold text-slate-950">You do not have access to course {courseId}</h1></main>;
    }
    return (
      <main className="flex-1 overflow-y-auto p-6">
        <h1 className="text-xl font-bold text-slate-950">Could not load {singular} transcript.</h1>
        <button className="mt-4 rounded-lg bg-slate-900 px-4 py-2 text-sm font-semibold text-white" onClick={() => setRetryToken((value) => value + 1)} type="button">Retry</button>
      </main>
    );
  }

  const { document, fetchedAt, transcript } = loadState;
  const returnedId = Number((document as unknown as Record<string, unknown>).id);
  const identityMismatch = !Number.isFinite(returnedId) || returnedId !== requestedId;
  const explicitlyMarked = transcript.steps.filter((step) => step.storedIsQuestion === true);
  const rawDocument = document as unknown as Record<string, unknown>;

  return (
    <main className="flex-1 overflow-y-auto bg-slate-100 px-4 py-8 text-slate-950 sm:px-8" data-testid="lesson-transcript-page">
      <div className="mx-auto max-w-5xl space-y-6">
        <header className="overflow-hidden rounded-2xl bg-slate-950 p-6 text-white shadow-xl sm:p-8">
          <p className="text-xs font-bold uppercase tracking-[0.2em] text-cyan-300">Current authoring document · read-only content view</p>
          <h1 className="mt-3 font-serif text-3xl font-semibold sm:text-4xl">{kind === 'entity' ? 'Lesson transcript' : 'Task transcript'}</h1>
          <p className="mt-2 break-words text-xl text-slate-100">{transcript.title || '(Untitled)'}</p>
          <RawStoredField label="title" raw={transcript.rawTitle} />
          <dl className="mt-6 grid gap-3 text-sm sm:grid-cols-2 lg:grid-cols-3">
            <div><dt className="text-slate-400">Course</dt><dd className="font-semibold">{courseId}</dd></div>
            <div><dt className="text-slate-400">Kind</dt><dd className="font-semibold">{kind === 'entity' ? 'Lesson' : 'Task'}</dd></div>
            <div><dt className="text-slate-400">Requested id</dt><dd className="font-semibold">{requestedId}</dd></div>
            <div><dt className="text-slate-400">Returned document id</dt><dd className="font-semibold">{Number.isFinite(returnedId) ? returnedId : 'Invalid stored value'}</dd></div>
            <div><dt className="text-slate-400">Publication</dt><dd className="font-semibold">{publicationLabel(transcript)}</dd></div>
            <div><dt className="text-slate-400">Stored steps</dt><dd className="font-semibold">{transcript.stepCount}</dd></div>
            <div><dt className="text-slate-400">Revision hash</dt><dd className="break-all font-mono text-xs">{typeof rawDocument._revisionHash === 'string' && rawDocument._revisionHash ? rawDocument._revisionHash : 'Not stated'}</dd></div>
            <div><dt className="text-slate-400">Fetched</dt><dd className="font-semibold">{fetchedAt.toLocaleString()}</dd></div>
            {kind === 'task' ? <div><dt className="text-slate-400">Difficulty</dt><dd className="font-semibold">{typeof rawDocument.difficulty === 'number' ? rawDocument.difficulty : 'Not stated'}</dd></div> : null}
          </dl>
          {/*
            These are stated as counts of OPTIONS OBSERVED, never as a verdict about the content.
            Both simpler phrasings were measured against lesson 1448 and both lie: its eleven steps
            all store `is_question: false` (so the document's own claim reports zero questions while
            two are plainly keyed), and six carry a single `Continue` (so "every step with options"
            reports six questions with no answer key). An unkeyed step is only reported where the
            learner is offered a real choice.
          */}
          <div className="mt-6 rounded-xl bg-white/10 p-4 text-sm leading-6 text-slate-100">
            <p>
              {transcript.keyedQuestionCount}{' '}
              {transcript.keyedQuestionCount === 1 ? 'step has' : 'steps have'} an option marked correct.
            </p>
            <p>
              {transcript.unkeyedQuestionCount}{' '}
              {transcript.unkeyedQuestionCount === 1 ? 'step offers' : 'steps offer'} a choice of two or
              more options with none marked correct.
            </p>
            <p>
              {transcript.singleOptionStepCount}{' '}
              {transcript.singleOptionStepCount === 1 ? 'step offers' : 'steps offer'} a single option to
              continue, which is not a question.
            </p>
            <p className="mt-2 text-slate-300">
              The document itself marks {explicitlyMarked.length} of its {transcript.stepCount} steps as
              questions via <code>is_question</code>
              {explicitlyMarked.length === 0 && transcript.keyedQuestionCount > 0
                ? ' — that field disagrees with the options above and is known to be unmaintained, so the options are what is shown here.'
                : '.'}
            </p>
          </div>
        </header>

        {identityMismatch ? <p className="rounded-xl border border-rose-300 bg-rose-50 p-4 font-semibold text-rose-950">Identity warning: requested id {requestedId}, but the returned document reports id {Number.isFinite(returnedId) ? returnedId : 'invalid stored value'}.</p> : null}

        <section className="rounded-2xl border border-cyan-200 bg-cyan-50 p-5 text-sm leading-6 text-cyan-950">
          <p>This is the current authoring API document fetched at the time shown above. Approved corrections may not have shipped yet, and this view does not include or imply pending edits.</p>
          <p className="mt-2 font-semibold">This page cannot edit or change {singular} content. Normal author-tool activity telemetry may still be recorded.</p>
        </section>

        {kind === 'task' ? <TaskLifecycle document={document} transcript={transcript} /> : null}

        {transcript.rawSummary !== null ? (
          <section className="rounded-2xl border border-slate-200 bg-white p-5">
            <h2 className="font-serif text-xl font-semibold">{kind === 'entity' ? 'Summary' : 'Snippet'}</h2>
            <p className="mt-2 whitespace-pre-wrap break-words text-sm leading-6 text-slate-800">{transcript.summary || '(Stored field has no plain text.)'}</p>
            <RawStoredField label={kind === 'entity' ? 'summary' : 'snippet'} raw={transcript.rawSummary} />
          </section>
        ) : null}

        {transcript.rawRcText !== null && transcript.rawRcText.trim() !== '' ? (
          <section className="rounded-2xl border border-slate-200 bg-white p-5">
            <h2 className="font-serif text-xl font-semibold">Shared reading passage (rc_text)</h2>
            <p className="mt-2 whitespace-pre-wrap break-words text-sm leading-6 text-slate-800">{transcript.rcText || '(Stored passage has no plain text.)'}</p>
            <RawStoredField label="rc_text" raw={transcript.rawRcText} />
          </section>
        ) : null}

        {kind === 'entity' ? <DependentsBlock state={dependencies} /> : null}
        <WarningList warnings={transcript.warnings} />

        {transcript.steps.length === 0 ? (
          <section className="rounded-2xl border border-amber-300 bg-amber-50 p-6 text-amber-950">This document stores zero steps.</section>
        ) : (
          <section className="space-y-5" aria-label="Stored steps">{transcript.steps.map((step) => <StepBlock key={`position-${step.position}`} step={step} />)}</section>
        )}

        {transcript.orphanResponses.length > 0 ? (
          <section className="rounded-2xl border-2 border-amber-300 bg-amber-50 p-5" data-testid="orphan-responses">
            <h2 className="font-serif text-xl font-semibold text-amber-950">Orphan response records ({transcript.orphanResponses.length})</h2>
            <p className="mt-2 text-sm text-amber-950">No stored step references these responses. Their text, key, feedback, and routes are retained below for review.</p>
            <ol className="mt-4 space-y-3">{transcript.orphanResponses.map((response, index) => <ResponseBlock key={`${response.responseId}-${index}`} orphan response={response} />)}</ol>
          </section>
        ) : null}
      </div>
    </main>
  );
}
